import React__default, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/600.css';
import '../common.scss.js';
import { warnOnce } from '../../utils/common.js';

var ThemeMode;
(function(ThemeMode2) {
  ThemeMode2["Light"] = "light";
  ThemeMode2["Dark"] = "dark";
})(ThemeMode || (ThemeMode = {}));
const ThemeContext = createContext(null);
const setHtmlAttributes = (theme, mode) => {
  document.documentElement.setAttribute("data-vai-theme", theme);
  document.documentElement.setAttribute("data-vai-theme-mode", mode);
};
const themes = [
  {
    name: "classic",
    modes: [ThemeMode.Light, ThemeMode.Dark],
    displayName: "Classic"
  },
  {
    name: "orca-noir",
    modes: [ThemeMode.Light, ThemeMode.Dark],
    displayName: "Orca Noir"
  },
  {
    name: "xweather",
    modes: [ThemeMode.Light],
    displayName: "Xweather"
  }
];
const defaultTheme = {
  name: "orca-noir",
  modes: [ThemeMode.Light, ThemeMode.Dark],
  displayName: "Orca Noir"
};
const ThemeProvider = (props) => {
  const { initialTheme = defaultTheme.name, initialMode = ThemeMode.Light, themes: themes2 = [defaultTheme], children } = props;
  const [appearance, setAppearance] = useState({
    theme: initialTheme,
    mode: initialMode
  });
  useEffect(() => {
    setHtmlAttributes(appearance.theme, appearance.mode);
  }, [appearance]);
  const setTheme = useCallback((theme) => {
    if (!themes2.some((t) => t.name === theme)) {
      warnOnce(`Theme "${theme}" does not exist. Valid values are [${themes2.map((t) => t.name)}]`);
      return;
    }
    setAppearance((prev) => {
      const themeObject = themes2.find((t) => t.name === theme);
      if (!themeObject.modes.includes(prev.mode)) {
        warnOnce(`Mode "${prev.mode}" is not supported for theme "${theme}". Valid modes are [${themeObject.modes}]. Falling back to ${themeObject.modes[0]}`);
        return { theme, mode: themeObject.modes[0] };
      }
      return { ...prev, theme };
    });
  }, [themes2]);
  const setMode = useCallback((mode) => {
    setAppearance((prev) => {
      const themeObject = themes2.find((t) => t.name === prev.theme);
      if (!themeObject.modes.includes(mode)) {
        warnOnce(`Mode "${mode}" is not supported for theme "${prev.theme}". Valid modes are [${themeObject.modes}]`);
        return prev;
      }
      return { ...prev, mode };
    });
  }, [themes2]);
  const contextValue = useMemo(() => {
    return { theme: appearance.theme, mode: appearance.mode, setMode, setTheme, themes: themes2 };
  }, [appearance, themes2, setMode, setTheme]);
  return /* @__PURE__ */ React__default.createElement(ThemeContext.Provider, {
    value: contextValue
  }, children);
};

export { ThemeContext, ThemeMode, ThemeProvider, defaultTheme, setHtmlAttributes, themes };
